/**
 * Read-box lore — bundled, offline-safe.
 *
 * The paged lore set shown in the landing read-box while the servers warm. Real
 * excerpts from the Tome (K'thar's Compendium, Ser Victa Reliu's Bestiary), plus
 * THE TOURNEY IN FULL — Prelude and Parts I-VII, unabridged.
 *
 * Per the storyboard note (frame 3) and ui-visual-canon: BUNDLED with the build
 * (fast, offline-safe) rather than fetched — the read-box is pure client and must
 * never block on or interfere with the socket during the most latency-sensitive
 * moment. Turning the cold-start wait into reading is the deliberate design.
 *
 * CANON SOURCE: docs/Tome/lore/_manuscripts/the-tourney.md v1.0.0 (the same text
 * ingested to plug_engine_reavers as the-tourney-lore + 8 chapters). The Tourney
 * pages below are VERBATIM from that manuscript — one paragraph per page, no
 * rewording. If the manuscript changes, this file changes with it; they are two
 * renderings of one text, and drift between them is a bug (2026-07-13-001-LORE).
 *
 * NOTE on canon: excerpt 2 ("Chief Among the Patterns: the Reavers") is the
 * queue's onboarding lore establishing that a "Reaver" is the ascended divine —
 * "not mere legends, nor distant deities, but a presence that thrums beneath the
 * skin of reality." This is what makes the Character-Choice framing honest: the
 * starting characters are mortals of Tritar, NOT Reavers.
 */

const TOURNEY = 'The Tourney';
const TOURNEY_AUTHOR = 'Year of our High King, 796';
const TOURNEY_PAGES = 24;

/** One Tourney page: verbatim manuscript paragraph, sequentially numbered. */
const t = (page, title, body, extra = {}) => ({
    tome: TOURNEY,
    author: TOURNEY_AUTHOR,
    page, of: TOURNEY_PAGES,
    title,
    body,
    ...extra,
});

export const LORE_PAGES = [
    {
        tome: "K'thar's Compendium",
        author: 'K’thar, Loremaster and Archivist of the Obsidian Libraries of Keth',
        page: 1, of: 9,
        title: 'A Complete Compendium of Aethe Knowledge',
        drop: 'T',
        body:
            'o the seeker, the scholar, or merely the curious soul who has stumbled upon these pages — ' +
            'greetings. Know that what lies within is but a fractured reflection of Aethe’s true visage, ' +
            'compiled from countless scrolls, whispered histories, and the desperate scrawls of those who ' +
            'bore witness to the passage of ages. Ours is a world defined by its echoes. The very dust ' +
            'beneath our boots hums with the memory of empires long fallen, of gods ascended and forgotten.',
    },
    {
        tome: "K'thar's Compendium",
        author: 'K’thar, Loremaster and Archivist of the Obsidian Libraries of Keth',
        page: 2, of: 9,
        title: 'Chief Among the Patterns: the Reavers',
        body:
            'They are not mere legends, nor distant deities, but a presence that thrums beneath the skin of ' +
            'reality, shaping the destinies of nations and the very nature of existence. Their designs are ' +
            'inscrutable, their motives shrouded in an antiquity that defies mortal comprehension — the ' +
            'unseen currents that drag entire civilizations into their wake, the silent architects of both ' +
            'burgeoning hope and profound ruin.',
    },
    {
        tome: 'The Bestiary of Tritar',
        author: 'Compiled by Ser Victa Reliu, Knight of the Realm',
        page: 5, of: 22,
        title: 'The Green Knight — Type: Unknown (Possible Reaver)',
        body:
            'A tall man, clad in armour the colour of emeralds, a surcoat woven with impossibly vibrant ' +
            'threads. His hair is the colour of moss; his eyes, a deep and unsettling summer blue. He does ' +
            'not speak, but merely watches — an ancient, patient waiting that felt older than the very ' +
            'trees. It was a look a man gives to a beetle he is about to crush. It is advised to avoid him ' +
            'at all costs.',
    },

    // ---- The Tourney, in full (manuscript v1.0.0) ----

    t(1, 'Prelude', null, {
        verse: [
            'Like tears of a maiden,',
            'down into the mud —',
            'spills the honor of kings,',
            'our offal and blood.',
        ],
    }),

    t(2, 'Part I',
        's Ser Ettan Jakes tugged the hardened leather glove tight over his spear hand he thought about ' +
        'the day. Winding through the crumbling arches of the ancient amphitheater death in the tourney ' +
        'was not top of mind. Bravery? Maybe. More likely — love, he decided for himself. His heavy ' +
        'stallion Rolo ambled with an easy grace as they rounded the arched frame of the champions gate. ' +
        'Above the well populated stone lists, shelves of colored finery rose; banners atop cracking in ' +
        'the wind. Red Lions and gold bears with claws at show, flying above the blues and greens of the ' +
        'simple dresses and dyed woolen caps.',
        { drop: 'A' }),

    t(3, 'Part I',
        'Taking up the reins, Ettan trotted Rolo down the line. His eyes scanned the high king’s box, a ' +
        'simple wood divide built on the ancient Korvek arena. Mae\'lin looked over the lanes causally — ' +
        'avoiding his gaze. The warmth from their long evening stroll was gone. A fist curled around ' +
        'Ettan’s heart. Her father ignored the field, fully erect in his seat, long gray hair in a ' +
        'top-knot, head turned slightly as he made conversation with King Bolas to his right. Bolas had ' +
        'played generous host these past days of the tourney and gestured widely as he spoke, leaning ' +
        'into the conversation. Ettan cared not for their machinations. He only had eyes for Mae\'lin.'),

    t(4, 'Part I',
        'As he approached the fear spiralled into his gut. Had their conversation been nothing more than ' +
        'a friendly talk at sunset? Ettan reviewed the spoken words against his unspoken feelings. Rolo ' +
        'interrupted his descent with a spirited whinney. Returning to the field of combat with a blank ' +
        'mind, Ettan smelled the cut grass and the mud of the lists. The ancient arches and pillars ' +
        'surrounding the field were reminders of an unknown past and a contrast to the simple Tritar ' +
        'banners and dress. It was 796 years after the first king of the Tritar united the warbands ' +
        'roving the fallen empire. He reigned Rolo, directly facing the high king. Ettan unsheathed his ' +
        'short broadsword ceremoniously into a salute.'),

    t(5, 'Part II',
        'High King Alidere gave a gentle gesture to King Bolas. It was time to acknowledge the first ' +
        'champion. He stood. Faced the gleaming knight on his dappled roan. Dark almond eyes took ' +
        'measure. Not a match for the old bear, Alidere thought to himself. Most likely going to be on ' +
        'his butt during round two. Won’t that get mud all over that well shined mail.'),

    t(6, 'Part II',
        'Princess Mae\'lin stood to acknowledge the young knight. Was that a rose in her hand? Where had ' +
        'that come from? And more to the point, why? Thoughts quickly wandered a long life. He had been ' +
        'an old man at her birth. What secrets and closeness had he missed with her? Oddly it was one of ' +
        'his few regrets.'),

    t(7, 'Part II',
        '“Ser Ettan Jakes,” she speaks clearly, handing her rose down. His young eyes brown saucers ' +
        'below. He hadn’t expected it, Alidere noticed. The awkward handling of his helmet and sword ' +
        'betrayed him. Handsome in his gold tinged dreadlocks, deep ebony skin. ‘Ser Ettan’, the king ' +
        'marked to follow up on his pedigrees.'),

    t(8, 'Part II',
        'Bowing his head, Ettan reins back. Handsome and proper, red rose in hand. He takes a moment to ' +
        'clip the rose to his mantle. Both the princess and her father sit again, demure. There is but a ' +
        'moment to catch the sparkling glance between the pair but his sly old man’s eyes see it without ' +
        'reaction. A delicate bit of life that spark. He lets out a breath, enjoying the morning of ' +
        'cloudy sunshine.'),

    t(9, 'Part III',
        'His worn armour feeling heavy this morning, Roak mused over the empire’s demise. Too old, he ' +
        'thinks — perhaps a bit of projection. Change is the only constant he thinks rounding the lists ' +
        'of the intricate gate to the arena. His opponent is backing from the salute to King Alidere. ' +
        'Squinting his heavy brows Roak picks out a bit of color in the young knight’s hand. He kicks ' +
        'the heavy black into a trot. The shadows of an early summer morning soft on the field.'),

    t(10, 'Part III',
        '“Ser Roak Orso, King of Wiesen, High Commander of the forces of Tritar.” The high king gives ' +
        'him his due. How many decades had they known each other? Alidere was a vibrant warrior when ' +
        'Roak cut his teeth in the war. Purging the Tritar kingdoms of foreign threat, Roak solidified ' +
        'his claim to Wiesen. The Havre bandits and raiding parties remained a rare occurrence and ' +
        'Roak’s patrols secured the long border of the wide blue river, Vortre, winding from Vardok’s ' +
        'Spine to the sea.'),

    t(11, 'Part III',
        'The peaceful days would not last the aging king’s passing though. Somehow Roak knew this in his ' +
        'gut. He met Alidere’s steady gaze, face inscrutable. He noticed the flush on the girl’s cheeks. ' +
        'He nodded to Alidere, backing his stallion. And began to think.'),

    t(12, 'Part III',
        'As he turned around the list to face Ser Ettan the sun was in his eyes. A glint, a flash. A ' +
        'future revealed itself in the pale sunshine. Ser Roak Orso reached down for his lance, his mind ' +
        'elsewhere as he brought the spear to his waist. This handsome boy down the field will be High ' +
        'King one day. It wasn’t a question in Roak’s mind.'),

    t(13, 'Part IV',
        'Ser Ettan judged the sun, the wind, the mud beneath Rolo. Roak’s silhouette was distant, heavy. ' +
        'As he lifted the lance to his side, he said one silent prayer to Father Light. Don’t flinch. A ' +
        'young man’s prayer. The flag dropped. And life’s moments compressed into a drumbeat.'),

    t(14, 'Part IV',
        'Deep, hooves in the mud, slap, slap, slap. Crack! Ser Roak Orso, the old bear. The lance ' +
        'shattered on the oak shield, pushing deep against Ettan’s chest as his body roiled, absorbing ' +
        'the beat. Abs clenching. Body — near horizontal springing backwards against the flank. Thump.'),

    t(15, 'Part IV',
        'Breath. Just breathe. A drawn course. Ettan dropped the broken lance behind the line and ' +
        'rounded. Breathe. Warm blood coursed through his shoulder, arm, wrist. Cool. Collected, Ettan ' +
        'scrubbed the front of the oak shield, recentered. He forwarded Rolo to the line. Took his ' +
        'second, the lance vertical as they stepped into the hole. Above the line of the tilt loomed ' +
        'Roak, a stout tree against the grey of the horizon.'),

    t(16, 'Part V',
        'Two broken lances. A tie with the young buck. Roak thought through the impact. It has been a ' +
        'heavy blow to the upper shoulder. He thought through tactics as he trotted the heavy stallion ' +
        'into the hole. Patient as he waited for the drop, the sunlight in his eyes not bothering his ' +
        'cool. These were the moments Roak loved. The meat of life, the focal points in an endless ' +
        'series of unserious moments. The inflections of destiny.'),

    t(17, 'Part V',
        'The takeoff was sturdy. His massive stallion heavy down the line. As he focused the distant ' +
        'shield grew rapidly. Lance dropping into place. Crunch. His weight into the point of impact. ' +
        'His own left shoulder rolling with the impact, the inevitable pains of age spreading hot ' +
        'through his joints. The young knight can take a hit. Roak gave him that. He tossed the broken ' +
        'lance as he rounded. One more turn.'),

    t(18, 'Part VI',
        'Rolling his shoulder Ethan wondered what it would take to unseat Ser Orso, this immovable lump ' +
        'of a man. He returned to his body letting the air and blood into clenched muscles. A tied ' +
        'contest preserved everyone’s honor, but it wasn’t gallant. Wouldn’t be remembered. Wouldn’t ' +
        'impress his worth on Mae\'lin. Ettan replayed the moments before impact. That little opening ' +
        'beyond Roak’s stout shield. How Roak had pulled up and in. A hint of possibility. Taking the ' +
        'last lance, Ettan rounded down to the hole.'),

    t(19, 'Part VI',
        'The flag dropped a satin blue glint. Time, motion, muscle and sound. It was a stretch that ' +
        'opening. From fifty yards Ettan took aim. The smooth beats of the gallop fading to the silence ' +
        'of intent.'),

    t(20, 'Part VI',
        'At five yards he was leaning full over his horse. Waiting for the shield to lift. As the lance ' +
        'tips passed Ettan knew. He grasped the mistake. Too late he tried to adjust. Roak’s lance ' +
        'slipped right along his shield, his beaten shoulder giving easily to the new angle. Time was ' +
        'coming in frames as Roak’s lance pierced his guts. Ettan was spared the gore of the moment. A ' +
        'trick of the mind. Blue skies and clouds, thoughts of Mae\'lin interrupted by the impact of the ' +
        'ground. A future drained away before his eyes, lifeblood soaking through the leather and mail ' +
        'into the dirt.'),

    t(21, 'Part VII',
        'As she snapped back into her body Mae\'lin heard her own scream as a distant wailing bell. In ' +
        'that moment beyond time had come an understanding so full it was difficult to fit into the ' +
        'horror of the moment.'),

    t(22, 'Part VII',
        'She watched as Ettan’s locks floated free of the helmet, his body slipping from the horse. A ' +
        'distant crunch of metal as mail met hard earth. She played out the events, it was her fault she ' +
        'felt. Agonizing pain a needle though her guts as Ettan bled out in the mud, squires and ' +
        'men-at-arms rushing to the field.'),

    t(23, 'Part VII',
        'Of two things she was certain. The first, Roak was a grim bastard and would be waiting for his ' +
        'next move. The three kingdoms would dissolve in the blood and chaos of war. He’d seen the truth ' +
        'of it. Without Ettan the succession was muddled. Queens sat uneasy thrones in the three ' +
        'kingdoms. Rarely had they been spared to rule unchallenged in the warrior’s society. Chivalry ' +
        'was an undressed ideal. Might was the iron of the nation’s will.'),

    t(24, 'Part VII',
        'Second she knew she would right this wrong. The blood debt was hers. As Ettan’s offal leaked ' +
        'into the field she knew. A rose, a thorn pierced innocence hardened into wrath. Let chaos ' +
        'reign, what care had she now. The peace of her father’s days already dissolved into a forgotten ' +
        'past inside her destiny. A flight, a fancy sprouted in the soil of her anguish. Not a plan, not ' +
        'yet. But a light to guide through the darkness that now gripped her soul.'),
];

/**
 * ReadBox — the paged lore reader. Pure client; owns only its own paging state.
 * escapeHtml is injected by the caller (shell.js) so this stays dependency-free.
 */
export class ReadBox {
    constructor({ escapeHtml } = {}) {
        this._idx = 0;
        this._el = null;
        this._esc = escapeHtml || ((s) => s);
    }

    /** Build the read-box DOM inside `host`. Returns the .readbox element. */
    mount(host) {
        const box = document.createElement('div');
        box.className = 'readbox';
        host.appendChild(box);
        this._el = box;
        this._render();
        // Event delegation for the pager buttons.
        box.addEventListener('click', (e) => {
            const btn = e.target.closest('.pager-btn');
            if (!btn || btn.classList.contains('disabled')) return;
            if (btn.dataset.dir === 'prev') this.prev();
            else if (btn.dataset.dir === 'next') this.next();
        });
        return box;
    }

    next() {
        if (this._idx < LORE_PAGES.length - 1) { this._idx++; this._render(); }
    }

    prev() {
        if (this._idx > 0) { this._idx--; this._render(); }
    }

    _render() {
        if (!this._el) return;
        const pg = LORE_PAGES[this._idx];
        const atStart = this._idx === 0;
        const atEnd = this._idx === LORE_PAGES.length - 1;
        const dropHtml = pg.drop
            ? `<span class="drop">${this._esc(pg.drop)}</span>`
            : '';
        // A verse page keeps its line breaks (the body path collapses them). Each
        // line is escaped independently; only the <br> separators are markup.
        const contentHtml = pg.verse
            ? `<span class="verse">${pg.verse.map((l) => this._esc(l)).join('<br>')}</span>`
            : `${dropHtml}${this._esc(pg.body)}`;
        this._el.innerHTML = `
            <div class="readbox-head">
                <span class="readbox-title">${this._esc(pg.tome)}</span>
                <span class="readbox-author">${this._esc(pg.author)}</span>
            </div>
            <div class="readbox-body">
                <span class="entry-title">${this._esc(pg.title)}</span>
                ${contentHtml}
            </div>
            <div class="readbox-foot">
                <span class="pager-btn${atStart ? ' disabled' : ''}" data-dir="prev">‹ PREV</span>
                <span>Page ${pg.page} / ${pg.of} — read on while the servers warm</span>
                <span class="pager-btn${atEnd ? ' disabled' : ''}" data-dir="next">NEXT ›</span>
            </div>`;
    }
}

export default ReadBox;
